<template>
  <section class="v-preview" ref="vPreview" @click.self="$emit('close')">
    <!-- 双联大卡片包装层：用于定位右上角关闭控制器 -->
    <div class="cinema-card-wrapper">
      <!-- 关闭按钮：位于箭头精确指示的大卡片右上角外沿，绝不压在目录导航上 -->
      <button class="cinema-corner-close-btn" @click="$emit('close')" aria-label="关闭预览" title="关闭 (Esc)">
        <v-icon small>mdi-close</v-icon>
      </button>

      <!-- 方案 A：画廊一体化剧场双联大卡片 -->
      <div class="cinema-modal-card" ref="cinemaCard">
        <!-- 左侧 72% 沉浸大舞台 -->
        <imagePreview
          class="cinema-stage-wrapper"
          :previewData="previewInfo"
          :current="current"
          :total="allCardList ? allCardList.length : 0"
          :isShowInfo="isShowInfo"
          @ccPrev="on_prev"
          @ccNext="on_next"
          @ccDownload="on_download"
          @ccToggleInfo="on_toggle_info"
        />

        <!-- 右侧 28% 一体化详情侧栏 -->
        <imageInfo
          class="cinema-sidebar-wrapper"
          v-if="Object.keys(previewInfo).length > 0"
          v-show="isShowInfo"
          :previewInfo="previewInfo"
          @ccClosePreview="on_close_preview"
          @ccUpdateInfo="load_preview_info"
          @ccToggleInfo="on_toggle_info"
          @ccUpdateListCardOperation="on_update_list_card_operation"
        />
      </div>
    </div>
  </section>
</template>

<script>
import { get_api_data } from "@/utils/ajax";
import imagePreview from "./compontents/image-preview/index";
import imageInfo from "./compontents/image-info/index";

// 内存单例详情缓存，实现 0ms 秒开与无缝切换
const detailCache = new Map();

export default {
  name: "v-preview",
  components: {
    imagePreview,
    imageInfo,
  },
  props: {
    originVue: {
      type: Object,
    },
    index: {
      type: Number,
      default: -1,
    },
  },
  created() {
    this.allCardList = (this.originVue && this.originVue.$store.state.global.allCardList) || [];
    this.fileId = (this.allCardList[this.index] && this.allCardList[this.index].fileId) || "";
    this.load_preview_info();

    // 监听键盘快捷键：ESC 退出、左箭头/A 上一张、右箭头/D 下一张（智能排除输入框聚焦场景）
    this._keyHandler = (e) => {
      const targetTag = (e.target && e.target.tagName) ? e.target.tagName.toUpperCase() : "";
      const isInput = targetTag === "INPUT" || targetTag === "TEXTAREA" || (e.target && e.target.isContentEditable);

      if (e.keyCode === 27) { // ESC 键
        this.$emit("close");
      } else if (!isInput && (e.keyCode === 37 || e.key === "ArrowLeft" || e.key === "a" || e.key === "A")) { // ← 或 A
        e.preventDefault();
        this.on_prev();
      } else if (!isInput && (e.keyCode === 39 || e.key === "ArrowRight" || e.key === "d" || e.key === "D")) { // → 或 D
        e.preventDefault();
        this.on_next();
      }
    };
    window.addEventListener("keydown", this._keyHandler);
  },
  beforeDestroy() {
    if (this._keyHandler) {
      window.removeEventListener("keydown", this._keyHandler);
    }
  },
  data() {
    return {
      fileId: "",
      current: this.index,
      isShowInfo: true,
      previewInfo: {},
    };
  },
  methods: {
    // 获取图片详情：列表已有分类路径、日期、大小、格式等参数 0ms 瞬间显示，再异步静默拉取完整评论与标签
    async load_preview_info() {
      const cardItem = this.allCardList[this.current] || {};
      const cached = detailCache.get(this.fileId);

      // 即时提取扩展名与建立时间
      const nameExt = cardItem.ext || (cardItem.name ? cardItem.name.split('.').pop().toUpperCase() : 'JPG');
      const baseCreated = cardItem.createdTime || cardItem.created_at || '';

      if (cached) {
        this.previewInfo = { ...cached };
      } else {
        // 即时装配列表已有的全部参数（包含后端直接返回的 path 与 createdTime），消除任何等待
        this.previewInfo = {
          ...cardItem,
          ext: nameExt,
          size: cardItem.size || '',
          createdTime: baseCreated,
          path: cardItem.path || '',
          driveId: cardItem.driveId || '',
          level1_fileId: cardItem.level1_fileId || '',
          fileId: this.fileId,
          preview_url: `https://drive.google.com/file/d/${this.fileId}/preview`,
          tags: cardItem.tags || [],
          comments: [],
        };
      }

      try {
        let imageInfo = await get_api_data("GET", `materials/${this.fileId}`);
        if (imageInfo && imageInfo.data) {
          detailCache.set(this.fileId, imageInfo.data);
          // 仅当当前选中的依然是此素材时才合并最新数据，避免翻页竞态覆盖
          if (this.fileId === imageInfo.data.fileId) {
            this.previewInfo = {
              ...this.previewInfo,
              ...imageInfo.data,
            };
          }
        }
      } catch (err) {
        console.warn("加载素材详情异常:", err);
      }

      // 静默预取前后相邻素材详情，消除后续翻页等待
      this.prefetchNeighbors();
    },

    // 预取当前素材前后相邻项的详情（使用标准 async/await，杜绝 promise 链式未定义报错）
    async prefetchNeighbors() {
      const len = this.allCardList.length;
      if (len <= 1) return;
      const prevIdx = this.current <= 0 ? len - 1 : this.current - 1;
      const nextIdx = this.current >= len - 1 ? 0 : this.current + 1;

      for (const idx of [prevIdx, nextIdx]) {
        const neighbor = this.allCardList[idx];
        if (neighbor && neighbor.fileId) {
          // 预载缩略图进入浏览器高速缓存，保障切图瞬间 0ms 呈现
          if (neighbor.thumbnail) {
            const img = new Image();
            img.src = neighbor.thumbnail;
          }
          if (!detailCache.has(neighbor.fileId)) {
            try {
              const res = await get_api_data("GET", `materials/${neighbor.fileId}`);
              if (res && res.data) {
                detailCache.set(neighbor.fileId, res.data);
              }
            } catch (e) {}
          }
        }
      }
    },

    // 点击tag后更新列表页数据，关闭预览组件
    async on_close_preview(tagName) {
      let tagCardListData = await get_api_data("GET", "search", {
        keyword: tagName,
        inc_tag: "onlytag",
      });

      this.originVue.$store._mutations["global/updateAllCardList"][0](
        tagCardListData.data.data
      );
      this.originVue.$store._mutations["global/updateAllCardListMeta"][0](
        tagCardListData.data.meta
      );

      this.$emit("close");
    },

    on_prev() {
      if (!this.allCardList || this.allCardList.length === 0) return;
      if (this.current <= 0) {
        this.current = this.allCardList.length - 1;
      } else {
        this.current--;
      }
      this.fileId = this.allCardList[this.current].fileId;
      this.load_preview_info();
    },

    on_next() {
      if (!this.allCardList || this.allCardList.length === 0) return;
      if (this.current >= this.allCardList.length - 1) {
        this.current = 0;
      } else {
        this.current++;
      }
      this.fileId = this.allCardList[this.current].fileId;
      this.load_preview_info();
    },

    // 下载素材
    async on_download() {
      let result = await get_api_data("POST", "materials/downloads", {
        fileIds: [this.fileId],
      });
      if (result.status === 200) {
        this.originVue.$store.state.global.allCardList[this.current].downloads++;
        window.open(this.allCardList[this.current].download_url, "_self");
      }
    },

    on_update_list_card_operation(type, value) {
      if (this.originVue.$store.state.global.allCardList[this.current]) {
        this.originVue.$store.state.global.allCardList[this.current][type] = value;
      }
    },

    on_toggle_info() {
      this.isShowInfo = !this.isShowInfo;
    },
  },
};
</script>

<style lang="scss">
.v-preview {
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 1000;
  background-color: rgba(5, 8, 16, 0.82);
  backdrop-filter: blur(24px) saturate(160%);
  -webkit-backdrop-filter: blur(24px) saturate(160%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  width: 100%;
  height: 100vh;
  box-sizing: border-box;
  animation: vPreviewFadeIn 0.24s cubic-bezier(0.16, 1, 0.3, 1) forwards;

  @keyframes vPreviewFadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  // 大卡片定位包裹层
  .cinema-card-wrapper {
    position: relative;
    width: min(1560px, 94vw);
    height: min(940px, 92vh);
    max-height: 92vh;
    display: flex;
    align-items: stretch;

    // 箭头指示位置：紧贴大卡片右上角外沿悬浮按键
    .cinema-corner-close-btn {
      position: absolute;
      top: -14px;
      right: -14px;
      z-index: 999;
      width: 38px;
      height: 38px;
      border-radius: 50%;
      background: rgba(15, 23, 42, 0.95);
      border: 1px solid rgba(255, 255, 255, 0.22);
      box-shadow: 0 8px 24px rgba(0, 0, 0, 0.65), 0 0 0 1px rgba(0, 0, 0, 0.4);
      backdrop-filter: blur(14px);
      -webkit-backdrop-filter: blur(14px);
      color: #e2e8f0;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.22s cubic-bezier(0.16, 1, 0.3, 1);
      outline: none;

      &:hover {
        background: rgba(239, 68, 68, 0.28);
        border-color: rgba(248, 113, 113, 0.6);
        color: #f87171;
        transform: rotate(90deg) scale(1.12);
        box-shadow: 0 8px 24px rgba(239, 68, 68, 0.45);
      }

      &:active {
        transform: rotate(90deg) scale(0.95);
      }
    }

    // 方案 A 核心：一体化双联剧场大卡片
    .cinema-modal-card {
      width: 100%;
      height: 100%;
      border-radius: 20px;
      background: rgba(13, 19, 33, 0.92);
      backdrop-filter: blur(36px) saturate(190%);
      -webkit-backdrop-filter: blur(36px) saturate(190%);
      border: 1px solid rgba(255, 255, 255, 0.1);
      box-shadow: 0 36px 90px -18px rgba(0, 0, 0, 0.85), 0 0 0 1px rgba(255, 255, 255, 0.05);
      display: flex;
      flex-direction: row;
      align-items: stretch;
      overflow: hidden;
      animation: cinemaCardScale 0.28s cubic-bezier(0.16, 1, 0.3, 1) forwards;

      @keyframes cinemaCardScale {
        from {
          opacity: 0;
          transform: scale(0.97) translateY(10px);
        }
        to {
          opacity: 1;
          transform: scale(1) translateY(0);
        }
      }
    }
  }
}
</style>